import { AppVersionLight } from '@zerops/models/app-version';
import { UserData } from '@zerops/models/user-data';
import { Project } from '@zerops/models/project';
import { ServiceStackCpuModes, ServiceStackHorizontalAutoscaling, ServiceStackVerticalAutoscaling } from '../settings';

export type ServiceStackTypeVersionStatus = 'ACTIVE' | 'DISABLED';

export enum ServiceStackModeList {
  HA = 'ha',
  SINGLE = 'single',
  FAILOVER = 'failover'
}

export const ServiceStackModesMap: Record<ServiceStackModeList, string> = {
    [ServiceStackModeList.HA]: "HA",
    [ServiceStackModeList.SINGLE]: "Single",
    [ServiceStackModeList.FAILOVER]: "Failover"
};

export class ServiceStackType {
  category: string;
  subcategory: string;
  created: string;
  description: string;
  id: ServiceStackTypes;
  lastUpdate: string;
  name: string;
  osList: ServiceStackTypeOperatingSystem[];
  modeList: ServiceStackModes[];
  hasBackup: boolean;
  hasAccessDetails: boolean;
  hasConfiguration: boolean;
  docsUrl: string;
  serviceStackTypeVersionList: ServiceStackTypeVersion[];
}

export class ServiceStackNextDefaultValue {
  type: string;
  name: string;
  version: string;
  mode?: string;
  startCommand?: string;
  documentRoot?: string;
  diskGBytes?: number;
  rawPolicy?: string;
  policy?: string;
  nginxConfig?: string;
  _versionName?: string;
  // TODO: interface
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ports?: any[];
  userData?: { key: string; content: string; }[];
  customAutoscaling?: ServiceStackAutoscaling;
}

export class ServiceStackTypeWithNextDefaultValue extends ServiceStackType {
  nextDefaultValue: ServiceStackNextDefaultValue;
}

export class ServiceStackTypeVersion {
  id: string;
  status: ServiceStackTypeVersionStatus;
  serviceStackTypeId: ServiceStackTypes;
  name: string;
  versionNumber: string;
  exactVersionNumber: string;
  description: string;
  driverId: string;
  created: string;
  lastUpdate: string;
  releaseDate: string;
  isRuntime: boolean;
  isManaged: boolean;
  isBuild: boolean;
  os: ServiceStackTypeOperatingSystem | null;
  mode: string | null;
  supportsVerticalAutoscaling: boolean;
  config?: ServiceStackConfig;
}

export class ServiceStackAutoscaling {
  horizontalAutoscaling?: ServiceStackHorizontalAutoscaling;
  // TODO -- ServiceStackVerticalAutoscaling
  verticalAutoscaling?: {
    cpuMode: ServiceStackCpuModes;
    startCpuCoreCount?: number;
    minFreeResource?: {
      cpuCoreCount?: number;
      cpuCorePercent?: number;
      memoryGBytes?: number;
      memoryPercent?: number;
    };
    maxResource: {
      cpuCoreCount: number;
      diskGBytes: number;
      memoryGBytes: number;
    };
    minResource: {
      cpuCoreCount: number;
      diskGBytes: number;
      memoryGBytes: number;
    };
  }
}

export interface AutoscalingProfile {
  id: string;
  name: string;
  description: string;
  order: number;
  isDefault: boolean;
  customAutoscaling: ServiceStackAutoscaling | null;
  overrides: Record<string, { name: string; dataType: AutoscalingProfileOverrideDataType; description: string }>;
}

export type AutoscalingProfileOverrideDataType = 'int' | 'float' | 'bool' | 'string' | 'byte' | 'nanosecond';

// Internal form representation is an object; the wire format is a JSON-stringified string
// (OpenAPI: format jsonRawMessage). Empty maps map to `null` (update) or omitted (create).
// Stringify/null/omit happens at the API/page boundary.
export type AutoscalingProfileOverridesValue = Record<string, string | number | boolean>;

export class ServiceStackConfig extends ServiceStackAutoscaling {
  verticalAutoscaling: ServiceStackVerticalAutoscaling;
  horizontalAutoscaling: ServiceStackHorizontalAutoscaling;
  defaultAutoscaling?: {
    horizontalAutoscaling: ServiceStackHorizontalAutoscaling;
    verticalAutoscaling: ServiceStackVerticalAutoscaling;
  }
  autoscalingProfiles?: AutoscalingProfile[];
  defaultWebserverConfig: string | null;
}

export enum ServiceStackStatuses {
  New = 'NEW',
  Creating = 'CREATING',
  Stopped = 'STOPPED',
  Active = 'ACTIVE',
  Deleting = 'DELETING',
  Deleted = 'DELETED',
  Failed = 'FAILED',
  Stopping = 'STOPPING',
  Starting = 'STARTING',
  Restarting = 'RESTARTING',
  Reloading = 'RELOADING',
  Upgrading = 'UPGRADING',
  Deploying = 'DEPLOYING',
  ActionFailed = 'ACTION_FAILED',
  ReadyToDeploy = 'READY_TO_DEPLOY',
  Repairing = 'REPAIRING',
  ContainerFailed = 'CONTAINER_FAILED',
  MovingContainer = 'MOVING_CONTAINER',
  // Statuses are artificial and not real from API
  NewBuildPending = 'NEW_BUILD_PENDING',
  ActiveBuildPending = 'ACTIVE_BUILD_PENDING',
  NewBuildRunning = 'NEW_BUILD_RUNNING',
  ActiveBuildRunning = 'ACTIVE_BUILD_RUNNING',
  NewAndInstalling = 'NEW_AND_INSTALLING'
}

export interface ServiceStackPort {
  port: number;
  protocol: string;
  description: string;
  scheme: 'tcp' | 'udp' | 'http' | 'https' | 'redis' | 'rabbitmq' | 'mysql';
  serviceId: string;
  portRouting: boolean;
  httpRouting: boolean;
}

export enum ServiceStackModes {
  Ha = 'HA',
  NonHa = 'NON_HA'
}

// TODO: refactor ServiceStack interface
export class ServiceStack {
  id: string;
  project: Project;
  projectId: string;
  instanceId: string;
  cdnEnabled?: boolean;
  subdomainAccess: boolean;
  activeAppVersion: AppVersionLight;
  serviceStackType: ServiceStackType;
  serviceStackTypeId: ServiceStackTypes;
  serviceStackTypeVersion: ServiceStackTypeVersion;
  serviceStackTypeVersionId: string;
  serviceStackTypeInfo: ServiceStackTypeInfo;
  hasUnsyncedPublicHttpRoutingRecord: boolean;
  hasUnsyncedPublicPortRecord: boolean;
  hasUnsyncedUserDataRecord: boolean;
  hasPublicPortRoutingAccess: boolean;
  hasPublicHttpRoutingAccess: boolean;
  versionNumber: string;
  status: ServiceStackStatuses;
  nginxConfig?: string;
  name: string;
  created: string;
  mode: ServiceStackModes;
  isSystem: boolean;
  lastUpdate: string;
  recipeSource?: string;
  recipeSourceUrl?: string;
  ports: ServiceStackPort[];
  requestedPorts?: {
    isActive: boolean;
    requestedPorts: ServiceStackPort[];
  };
  githubIntegration: ServiceStackGithubIntegration;
  // TODO: interface
  gitlabIntegration: ServiceStackGithubIntegration;
  reloadAvailable: boolean;
  userData: UserData[];
  parameterList: Array<{
    parameter: string;
    content: string;
  }>;
  coreService: {
    id: string;
    currentActiveContainerCount: number;
    targetContainerCount :number;
  };
  customAutoscaling: ServiceStackAutoscaling;
  currentAutoscaling: ServiceStackAutoscaling;
  autoscalingProfileId?: string;
  autoscalingProfileOverrides?: AutoscalingProfileOverridesValue | null;
}

export class ServiceStackGithubIntegration {
  authorizationRequired: boolean;
  branchName: string;
  eventType: string;
  isActive: boolean;
  repositoryFullName: string;
  userId: string;
  zeropsSetup?: string;
  webhookId: number;
}

export class ServiceStackFuse extends ServiceStack {
  _serviceStackTypeName: string;
  _serviceStackVersionNumber: string;
}

interface ServiceStackBackupMetadata {
  file_name: string;
  stack_name: string;
  mode?: 'AUTOMATIC' | 'MANUAL';
  tags: string[];
}

export interface ServiceStackBackup {
  metadata: ServiceStackBackupMetadata;
  name: string;
  path: string;
  size: number;
}

export interface ProjectBackup extends ServiceStackBackup {
  created: string;
  serviceStackId: string;
  serviceStackName: string;
  serviceStackDeleted: boolean;
}

export interface ProjectBackupListResponse {
  list: ProjectBackup[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface ServiceStackBackupResponse {
  backupPeriod: string;
  files: ServiceStackBackup[];
  defaultRetentionPolicy: ServiceStackBackupRetentionPolicy;
  retentionPolicy?: ServiceStackBackupRetentionPolicy;
}

export interface ServiceStackBackupRetentionPolicy {
  maxDaily: number;
  maxMonthly: number;
  maxTotalFiles: number;
  maxTotalGiB: number;
  maxWeekly: number;
  minDaily: number;
  minMonthly: number;
  minWeekly: number;
  protectedTags: string[];
}

export interface ServiceStackBackupConfig {
  backupPeriod: string;
  retentionPolicy: ServiceStackBackupRetentionPolicy;
}

export enum ObjecStorageAcls {
  None = 'none',
  Private = 'private',
  PublicRead = 'public-read',
  PublicObjectsRead = 'public-objects-read',
  PublicReadWrite = 'public-read-write',
  PublicWrite = 'public-write',
  AuthenticatedRead = 'authenticated-read',
  Custom = 'custom'
}

export enum ServiceStackTypeCategories {
  Core = 'CORE',
  Build = 'BUILD',
  Prepare = 'PREPARE_RUNTIME',
  HttpL7Balancer = 'HTTP_L7_BALANCER',
  Standard = 'STANDARD',
  User = 'USER',
  ObjectStorage = 'OBJECT_STORAGE',
  LocalStorage = 'LOCAL_STORAGE'
}

export enum ServiceStackTypes {
  Elasticsearch = 'elasticsearch',
  Meilisearch = 'meilisearch',
  Typesense = 'typesense',
  Redis = 'keydb',
  Valkey = 'valkey',
  Qdrant = 'qdrant',
  Nats = 'nats',
  Kafka = 'kafka',
  MariaDb = 'mariadb',
  RabbitMq = 'rabbitmq',
  MongoDb = 'mongodb',
  PhpNginx = 'php_nginx',
  PhpApache = 'php_apache',
  Nginx = 'nginx',
  NodeJs = 'nodejs',
  Ubuntu = 'ubuntu',
  Alpine = 'alpine',
  Docker = 'docker',
  Postgres = 'postgresql',
  Clickhouse = 'clickhouse',
  Golang = 'golang',
  ObjectStorage = 'object_storage',
  Seaweedfs = 'seaweedfs',
  LocalStorage = 'local_storage',
  DotNet = 'dotnet',
  Rust = 'rust',
  Python = 'python',
  Static = 'static',
  Bun = 'bun',
  Deno = 'deno',
  Java = 'java',
  Elixir = 'elixir',
  Gleam = 'gleam',
  Ruby = 'ruby',
  Zcp = 'zcp'
}

export type ServiceStackTypeOperatingSystem = 'alpine' | 'ubuntu';

export class ServiceStackTypeInfo {
  serviceStackTypeName: string;
  serviceStackTypeVersionName: string;
  serviceStackTypeCategory: ServiceStackTypeCategories;
}

export class ServiceStackTypesWithServiceStackStatuses {
  id: string;
  data: {
    name: string;
    serviceStackTypeName: string;
    serviceStackTypeVersionName: string;
    serviceStackType: ServiceStackType;
    serviceStackTypeVersion: ServiceStackTypeVersion;
  };
  statuses: {
    amount: number;
    label: string;
  }[];
}

export class ServiceStackUpdateData {
  startCommand?: string;
  initCommands?: string[];
  nginxConfig?: string;
}

export class TriggerPipelinePayload {

}

export type MetricTypes = 'cpu' | 'vCpu' | 'ramGBytes' | 'diskGBytes' | 'spend';

export enum MetricTypesEnum {
  Cpu = 'cpu',
  VCpu = 'vCpu',
  Ram = 'ramGBytes',
  Disc = 'diskGBytes'
}

export const defaultMetricTypes = [ 'cpu', 'ramGBytes', 'diskGBytes' ];

export interface ServiceStackAutoscalingHAContainers {
  appContainers: number;
  freeContainers: number;
}

export interface ServiceStackAutoscalingConfigItem {
  horizontal: boolean;
  vertical: boolean;
  mode: boolean;
  HAContainers?: ServiceStackAutoscalingHAContainers;
}

const HA_ADDITIONAL_CONTAINERS: Record<string, { appContainers: number; additionalContainers: number }> = {
  postgresql: { appContainers: 3, additionalContainers: 2 },
  mariadb: { appContainers: 3, additionalContainers: 2 },
  rabbitmq: { appContainers: 3, additionalContainers: 2 },
  seaweedfs: { appContainers: 2, additionalContainers: 0 },
};

export function getHAContainers(
  serviceTypeId: string,
  modeList?: string[]
): ServiceStackAutoscalingHAContainers | undefined {
  const override = HA_ADDITIONAL_CONTAINERS[serviceTypeId];
  if (override) {
    return { appContainers: override.appContainers, freeContainers: override.additionalContainers };
  }
  if (modeList?.length > 1) {
    return { appContainers: 3, freeContainers: 0 };
  }
  return undefined;
}

export const RABBIT_MQ_WEB_MANAGEMENT_PORT = 15672;
export const RABBIT_MQ_WS1_PORT = 15674;
export const RABBIT_MQ_WS2_PORT = 15675;
export const RABBIT_MQ_SERVICE_STACK_TYPE_NAME = 'RabbitMQ';
